import {
  onCLS,
  onLCP,
  onINP,
  onFCP,
  onTTFB,
  type Metric,
  type LCPMetricWithAttribution,
} from "web-vitals/attribution";
import { supabase } from "@/integrations/supabase/client";

function deviceType(): "mobile" | "tablet" | "desktop" {
  const w = window.innerWidth;
  if (w < 640) return "mobile";
  if (w < 1024) return "tablet";
  return "desktop";
}

function getSessionId(): string {
  try {
    let id = sessionStorage.getItem("pywl_vitals_sid");
    if (!id) {
      id = crypto.randomUUID();
      sessionStorage.setItem("pywl_vitals_sid", id);
    }
    return id;
  } catch {
    return "anon";
  }
}

// Collapse dynamic segments so charts group by template, not by id.
function normalizePath(path: string): string {
  return path
    .replace(/\/property\/[^/]+/, "/property/:id")
    .replace(/\/collection\/[^/]+/, "/collection/:slug")
    .replace(/\/blog\/[^/]+/, "/blog/:slug")
    .replace(/\/reviews\/[^/]+/, "/reviews/:propertyId");
}

const sent = new Set<string>();

function logToConsole(metric: Metric) {
  // Human-readable trace so we can eyeball hero LCP before/after the
  // blurred placeholder change without opening the database.
  const base = `[web-vitals] ${metric.name} ${Math.round(metric.value)}${
    metric.name === "CLS" ? "" : "ms"
  } (${metric.rating})`;
  if (metric.name === "LCP") {
    const attr = (metric as LCPMetricWithAttribution).attribution;
    console.info(base, {
      element: attr?.target,
      url: attr?.url,
      timeToFirstByte: Math.round(attr?.timeToFirstByte ?? 0),
      resourceLoadDelay: Math.round(attr?.resourceLoadDelay ?? 0),
      resourceLoadDuration: Math.round(attr?.resourceLoadDuration ?? 0),
      elementRenderDelay: Math.round(attr?.elementRenderDelay ?? 0),
    });
  } else {
    console.info(base);
  }
}

async function send(metric: Metric) {
  // Each metric is reported once per page load, but onCLS/onLCP can fire updates
  // as the page evolves. Use a per-id key so we only insert once per measurement.
  if (sent.has(metric.id)) return;
  sent.add(metric.id);

  logToConsole(metric);

  try {
    await supabase.from("web_vitals").insert({
      metric_name: metric.name,
      value: metric.value,
      rating: metric.rating,
      page_path: normalizePath(window.location.pathname),
      navigation_type: metric.navigationType ?? null,
      device_type: deviceType(),
      session_id: getSessionId(),
      user_agent: navigator.userAgent.slice(0, 255),
    });
  } catch (err) {
    // Never let analytics break the app.
    console.warn("[web-vitals] insert failed", err);
  }
}

let started = false;
export function startWebVitalsReporting() {
  if (started || typeof window === "undefined") return;
  started = true;
  // Skip admin routes — internal traffic skews the numbers.
  if (window.location.pathname.startsWith("/admin")) return;

  onLCP(send);
  onCLS(send);
  onINP(send);
  onFCP(send);
  onTTFB(send);
}